import { useEffect } from 'react'

const BOOTSTRAP_SCRIPT_URL =
  'https://lyrahealthservice--bot.sandbox.my.site.com/ESWInAppWeb1726095732551/assets/js/bootstrap.min.js'

const USER_CHAT_EXPIRATION_TIMESTAMP = 'USER_CHAT_EXPIRATION_TIMESTAMP'

declare global {
  interface Window {
    hasLoadedInAppMessaging: boolean
    shouldDisallowCountdownReset: boolean
    USER_CHAT_EXPIRATION_TIMESTAMP: string
    __inAppMessagingScriptLoaded?: boolean
    embeddedservice_bootstrap?: any
  }
}

/**
 * Hook to load the In-App Messaging script for web applications.
 * This hook handles the dynamic loading of the Salesforce Embedded Messaging script
 * and initializes it with the appropriate configuration based on the environment.
 *
 * @param shouldInitialize - Boolean indicating if the script should be loaded (default: true)
 */
export function useInAppMessagingScript(shouldInitialize = true) {
  useEffect(() => {
    let script: HTMLScriptElement | undefined

    const handleEmbeddedMessagingReady = () => {
      // Clear all chats iff the user's last sent message occurred after the current time
      const timestampOfLastSentMessage = localStorage.getItem(window.USER_CHAT_EXPIRATION_TIMESTAMP)
      if (timestampOfLastSentMessage && Date.now() >= Date.parse(timestampOfLastSentMessage)) {
        window.embeddedservice_bootstrap?.userVerificationAPI?.clearSession?.()
        localStorage.removeItem(window.USER_CHAT_EXPIRATION_TIMESTAMP)
      }
      window.hasLoadedInAppMessaging = true
    }

    const handlePageHide = () => {
      window.embeddedservice_bootstrap?.userVerificationAPI?.clearSession?.()
    }

    // Load conditionally based on `shouldInitialize` and href of current page
    if (shouldInitialize && window.location.href.indexOf('ssoregistration') <= -1) {
      // Initialize global variables (only if not already set to preserve state)
      if (window.hasLoadedInAppMessaging === undefined) {
        window.hasLoadedInAppMessaging = false
      }
      window.USER_CHAT_EXPIRATION_TIMESTAMP = USER_CHAT_EXPIRATION_TIMESTAMP

      // Only load once
      if (window.__inAppMessagingScriptLoaded) {
        return
      }
      window.__inAppMessagingScriptLoaded = true

      const initEmbeddedMessaging = () => {
        try {
          const embeddedservice_bootstrap = window.embeddedservice_bootstrap
          if (!embeddedservice_bootstrap) {
            console.error('embeddedservice_bootstrap not available')
            return
          }

          embeddedservice_bootstrap.settings.language = 'en_US'
          embeddedservice_bootstrap.settings.hideChatButtonOnLoad = true

          window.addEventListener('onEmbeddedMessagingReady', handleEmbeddedMessagingReady)

          // Try to clear all chats if the browser fires a `pagehide` event (i.e. the user closes the tab or window)
          window.addEventListener('pagehide', handlePageHide)

          const testEnvironments = ['localhost', 'staging', 'dev', 'prerelease']
          const isTestEnvironment = testEnvironments.some((environmentName) =>
            window.location.host.includes(environmentName),
          )

          if (isTestEnvironment) {
            embeddedservice_bootstrap.init(
              '00DWH000001WFxp',
              'InAppWeb',
              'https://lyrahealthservice--full.sandbox.my.site.com/ESWInAppWeb1731346250499',
              {
                scrt2URL: 'https://lyrahealthservice--full.sandbox.my.salesforce-scrt.com',
              },
            )
          } else {
            embeddedservice_bootstrap.init(
              '00D6g000001WM8t',
              'InAppWeb',
              'https://lyrahealthservice.my.site.com/ESWInAppWeb1731346250499',
              {
                scrt2URL: 'https://lyrahealthservice.my.salesforce-scrt.com',
              },
            )
          }
        } catch (err) {
          console.error('Error loading Embedded Messaging: ', err)
        }
      }

      script = document.createElement('script')
      script.src = BOOTSTRAP_SCRIPT_URL
      script.async = true
      script.onload = () => {
        initEmbeddedMessaging()
      }
      document.body.appendChild(script)
    }

    return () => {
      if (script && script.parentNode) {
        script.parentNode.removeChild(script)
        window.__inAppMessagingScriptLoaded = false
      }
      window.removeEventListener('onEmbeddedMessagingReady', handleEmbeddedMessagingReady)
      window.removeEventListener('pagehide', handlePageHide)
    }
  }, [shouldInitialize])
}
